import React from 'react'
import { Element, Icon, Loader } from 'react-bulma-components'
import { User } from '../../api/users'
import { WeeksSummaryStatus } from '../../api/missions'
import Avatar from '../../components/avatar/avatar'
import { BanIcon, MissionWeekValidatedIcon, WarningIcon } from '../../components/icons'
import { compareDocsById } from '../../utils/documents'
import { CheckCircleIcon } from '@heroicons/react/outline'
import { sortByIgnoreCase } from '../../utils/arrays'

/**
 * Props pour le composant WorkersList
 */
interface WorkersListProps {
  /** Liste des travailleurs à afficher */
  workers: User[]
  /** Statut des semaines (recorded/validated) pour chaque travailleur */
  weeksSummaryStatus?: WeeksSummaryStatus
  /** Fonction appelée quand un travailleur est sélectionné */
  onWorkerSelect: (worker: User) => void
  activeWorkerId?: string
  isLoading?: boolean
  /** Afficher uniquement les workers avec des semaines non enregistrées */
  showOnlyUnrecordedWeeks?: boolean
}

/**
 * Composant pour afficher la liste des travailleurs avec leur statut de validation
 *
 * Affiche chaque travailleur avec son avatar et une icône de validation
 * si toutes les semaines sont enregistrées.
 */
const WorkersList: React.FC<WorkersListProps> = ({
  workers,
  weeksSummaryStatus,
  onWorkerSelect,
  activeWorkerId,
  isLoading,
  showOnlyUnrecordedWeeks = false,
}) => {
  const sortedWorkers = sortByIgnoreCase(workers, 'lastName')

  // Filtrer les workers selon le critère showOnlyUnrecordedWeeks
  const filteredWorkers = showOnlyUnrecordedWeeks
    ? sortedWorkers.filter(worker => {
        const statusForWorker = weeksSummaryStatus?.users?.find(u =>
          compareDocsById(u.user, worker),
        )
        // Afficher seulement les workers qui ont des semaines non enregistrées
        return statusForWorker && statusForWorker.recordedWeeks < statusForWorker.weeks.length
      })
    : sortedWorkers

  if (isLoading) {
    return <Loader />
  }

  if (filteredWorkers.length === 0) {
    return (
      <Element mt={5} textAlign={'center'}>
        <Icon>
          <BanIcon />
        </Icon>
        <Element textSize={7}>Aucun salarié correspondant aux filtres</Element>
      </Element>
    )
  }

  return (
    <>
      {filteredWorkers?.map(worker => {
        // Récupérer le statut des semaines pour ce travailleur via WeeksSummaryStatus
        const statusForWorker = weeksSummaryStatus?.users?.find(u =>
          compareDocsById(u.user, worker),
        )
        // Calculer les agrégats d'état
        const allRecorded = statusForWorker?.recordedWeeks === statusForWorker?.weeks.length
        const allValidated = statusForWorker?.validatedWeeks === statusForWorker?.weeks.length
        const weeksToValidate =
          (statusForWorker?.totalWeeks || 0) - (statusForWorker?.validatedWeeks || 0)

        return (
          <Element
            key={worker._id}
            py={3}
            onClick={() => onWorkerSelect(worker)}
            style={{ cursor: 'pointer', borderBottom: '1px solid #e0e0e0' }}
            backgroundColor={activeWorkerId === worker._id ? 'light' : 'transparent'}
            data-test={`workers-list-item-${worker._id}`}
          >
            <Element pl={2}>
              <Element textWeight="semibold">
                <Avatar user={worker} />
              </Element>
              {allRecorded && (
                <Icon.Text>
                  <Icon color="success" size="small" mt={1}>
                    <MissionWeekValidatedIcon />
                  </Icon>
                  <Element textSize={7}>Période cloturée</Element>
                </Icon.Text>
              )}
              {!allRecorded && (
                <>
                  <Element textSize={7}>
                    {statusForWorker?.recordedWeeks}/{statusForWorker?.weeks.length} semaine(s)
                    cloturée(s)
                  </Element>
                  <Icon.Text>
                    {!allValidated && (
                      <>
                        <Icon color="danger" size="small" mt={1}>
                          <WarningIcon />
                        </Icon>
                        <Element textSize={7}>
                          {weeksToValidate} semaine(s) à valider avant cloture
                        </Element>
                      </>
                    )}
                    {allValidated && (
                      <>
                        <Icon color="warning" size="small" mt={1}>
                          <CheckCircleIcon />
                        </Icon>
                        <Element textSize={7}>Période prête à être clôturée</Element>
                      </>
                    )}
                  </Icon.Text>
                </>
              )}
            </Element>
          </Element>
        )
      })}
    </>
  )
}

export default WorkersList
